import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { TopBar } from "../components/Navigation";
import { BadgeCheck, Mountain } from "lucide-react";
import { resolveImageUrl } from "../components/ImageUploader";

function partnerSinceLabel(iso) {
    if (!iso) return "Recently joined";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    } catch { return "Recently joined"; }
}

export default function Brands() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/brands");
                if (!cancelled) setItems(data.items || []);
            } catch {
                if (!cancelled) setItems([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <div>
            <TopBar title="Brands" />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-12">
                <div className="mb-10">
                    <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-3">
                        Partners
                    </div>
                    <h1 className="font-display font-black text-4xl lg:text-6xl text-midnight-pine tracking-tight mb-4 max-w-3xl leading-tight">
                        Brands the RealWorld Outdoors community trusts.
                    </h1>
                    <p className="text-base lg:text-lg text-slate-gray max-w-2xl">
                        Every brand here has been vetted by our admin team. Pack, fuel, layer —
                        these are the names showing up on real adventures.
                    </p>
                </div>

                {loading ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="h-64 bg-snowcap border border-ridge-line rounded-xl animate-pulse" />
                        ))}
                    </div>
                ) : items.length === 0 ? <EmptyState /> : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {items.map((b) => <BrandCard key={b.user_id} b={b} />)}
                    </div>
                )}
            </div>
        </div>
    );
}

function BrandCard({ b }) {
    const coverUrl = resolveImageUrl(b.hero_image_url || b.latest_image_url);
    return (
        <Link
            to={`/brands/${b.user_id}`}
            data-testid={`brand-card-${b.user_id}`}
            className="group bg-snowcap border border-ridge-line rounded-xl overflow-hidden hover:border-midnight-pine hover:shadow-lg transition-all flex flex-col"
        >
            {coverUrl ? (
                <div className="aspect-[16/9] bg-alpine-mist overflow-hidden">
                    <img
                        src={coverUrl}
                        alt=""
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                    />
                </div>
            ) : (
                <div className="aspect-[16/9] bg-gradient-to-br from-trail-tan/40 to-alpine-mist flex items-center justify-center">
                    <Mountain className="w-12 h-12 text-slate-gray opacity-40" />
                </div>
            )}
            <div className="p-5 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-display font-bold text-lg text-midnight-pine leading-tight truncate">
                        {b.name}
                    </h3>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-deep-evergreen/15 text-deep-evergreen text-[10px] font-bold uppercase tracking-widest shrink-0">
                        <BadgeCheck className="w-3 h-3" /> Live
                    </span>
                </div>
                <div className="text-xs text-slate-gray mb-3">
                    {b.location ? `${b.location} · ` : ""}Partner since {partnerSinceLabel(b.partner_since)}
                </div>
                {(b.tagline || b.latest_headline) && (
                    <p className="text-sm text-granite line-clamp-2 flex-1">{b.tagline || b.latest_headline}</p>
                )}
            </div>
        </Link>
    );
}

function EmptyState() {
    return (
        <div data-testid="brands-empty" className="bg-snowcap border border-dashed border-ridge-line rounded-xl p-12 text-center">
            <Mountain className="w-12 h-12 text-slate-gray opacity-40 mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl text-midnight-pine mb-2">
                No brand partners yet.
            </h3>
            <p className="text-slate-gray text-sm max-w-md mx-auto">
                Brand partners are curated by the RealWorld Outdoors team. Check back soon — new
                partners are added regularly.
            </p>
        </div>
    );
}
